import { H3HexagonLayer } from 'deck.gl';

import { MATRIZ_RGBA, clasificaTipologia } from '../tipologia';

// La grilla base se puede colorear por cuatro variables (modo):
//   accesibilidad: indice E2SFCA del alfa elegido, rampa azul (0 a 1).
//   poblacion:     residentes de la celda (Censo 2024), rampa dorada.
//   trabajadores:  trabajadores estimados en la celda, rampa naranja-roja.
//   tipologia:     coropleto bivariado del panel A de la figura, que cruza
//                  accesibilidad retenida (alfa 0.5) con peso de la poblacion
//                  trabajadora. Es el modo que le da sentido a la matriz de
//                  la leyenda, y el unico fijo en alfa 0.5.
// El dorado y el naranja siguen el codigo del resto de la interfaz
// (residentes = primer orden, trabajadores = segundo orden). La opacidad sube
// con el valor: las celdas altas se ven solidas, las bajas dejan pasar el
// basemap.
const RAMPAS = {
  accesibilidad: { desde: [12, 32, 64], hasta: [96, 168, 255] },
  poblacion: { desde: [40, 28, 8], hasta: [255, 196, 64] },
  trabajadores: { desde: [40, 14, 8], hasta: [255, 110, 60] },
};

// La bivariada codifica sus dos variables en el color, asi que aqui no se
// modula nada mas: MATRIZ_RGBA ya trae la adaptacion al basemap oscuro.

const GRIS_SIN_DATO = [42, 42, 51, 60];
const OPACIDAD_MINIMA = 70;
const OPACIDAD_MAXIMA = 210;

const DORADO_ORIGEN = [255, 213, 74, 255];

const ANCHO_LINEA_ORIGEN = 3;
const ANCHO_LINEA_BASE = 1;

// La reticula hexagonal es la marca visual de la interfaz: una linea fina
// sobre todas las celdas, incluidas las que no tienen dato. El dato va en el
// relleno, que ademas es el blanco del click: con el hexagono vacio no hay
// donde apuntar. Se probo la variante inversa (relleno transparente y color
// en el borde) y no funciona a escala ciudad, donde la celda mide unos 8 px y
// un borde grueso ocupa media celda.
const LINEA_TENUE = [110, 122, 145, 105];
// En tipologia la reticula toma el color de la clase, asi que el tono cambia
// en el espacio como en la figura, pero el relleno sigue llevando la lectura.
// El canto va mas brillante y mas opaco que su relleno, para que el hexagono
// tenga borde nitido, y escala con la opacidad del relleno en vez de fijarse:
// la clase desatendida es casi blanca y con un canto fijo se apodera de la
// vista de ciudad, que es donde esas celdas son la mayoria.
const BRILLO_CANTO = 1.2;
const OPACIDAD_CANTO = 1.7;

function canto([r, g, b, a]) {
  const brillo = (c) => Math.min(255, Math.round(c * BRILLO_CANTO));
  return [brillo(r), brillo(g), brillo(b), Math.min(255, Math.round(a * OPACIDAD_CANTO))];
}

function interpola(rampa, t) {
  const v = Math.max(0, Math.min(1, t));
  const { desde, hasta } = rampa;
  return [
    Math.round(desde[0] + (hasta[0] - desde[0]) * v),
    Math.round(desde[1] + (hasta[1] - desde[1]) * v),
    Math.round(desde[2] + (hasta[2] - desde[2]) * v),
    Math.round(OPACIDAD_MINIMA + (OPACIDAD_MAXIMA - OPACIDAD_MINIMA) * v),
  ];
}

// Valor normalizado a [0,1] de la celda segun el modo. La accesibilidad ya
// viene en [0,1]; los conteos se normalizan con la raiz de valor/maximo para
// que las celdas bajas (la mayoria) no queden todas oscuras.
function valorNormalizado(d, modo, alpha, maxima) {
  if (modo === 'poblacion') {
    const max = maxima?.poblacion || 1;
    return Math.sqrt(Math.min(1, (d.pop_vivienda ?? 0) / max));
  }
  if (modo === 'trabajadores') {
    const max = maxima?.trabajadores || 1;
    return Math.sqrt(Math.min(1, (d.pop_trabajo ?? 0) / max));
  }
  const rating = d.ratings?.[alpha]?.total;
  if (rating === null || rating === undefined || Number.isNaN(rating)) return null;
  return rating;
}

function colorTipologia(d) {
  const acc50 = d.ratings?.['050']?.total;
  if (acc50 === null || acc50 === undefined || Number.isNaN(acc50)) return GRIS_SIN_DATO;
  const { accClass, pesoClass } = clasificaTipologia(acc50, d.peso);
  return MATRIZ_RGBA[accClass][pesoClass];
}

export function construyeCapaGrilla({ grilla, alpha, modo = 'accesibilidad', maxima, origenH3, onClick }) {
  const rampa = RAMPAS[modo] ?? RAMPAS.accesibilidad;
  return new H3HexagonLayer({
    id: 'grilla',
    data: grilla,
    pickable: true,
    extruded: false,
    filled: true,
    stroked: true,
    highPrecision: 'auto',
    getHexagon: (d) => d.h3,
    getFillColor: (d) => {
      if (modo === 'tipologia') return colorTipologia(d);
      const v = valorNormalizado(d, modo, alpha, maxima);
      return v === null ? GRIS_SIN_DATO : interpola(rampa, v);
    },
    getLineColor: (d) => {
      if (d.h3 === origenH3) return DORADO_ORIGEN;
      if (modo !== 'tipologia') return LINEA_TENUE;
      return canto(colorTipologia(d));
    },
    getLineWidth: (d) => (d.h3 === origenH3 ? ANCHO_LINEA_ORIGEN : ANCHO_LINEA_BASE),
    lineWidthUnits: 'pixels',
    lineWidthMinPixels: ANCHO_LINEA_BASE,
    updateTriggers: {
      getFillColor: [alpha, modo, maxima],
      getLineColor: [origenH3, modo],
      getLineWidth: [origenH3],
    },
    onClick: (info) => info.object && onClick(info.object.h3),
  });
}
